import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import {
  CreateAction,
  CreateFailureAction,
  CreateSuccessAction,
  DeleteAction,
  StatusActionTypes,
  SaveAction,
  SaveFailureAction,
  SaveSuccessAction,
  SearchFailureAction,
  SearchSuccessAction,
  SortSuccessAction,
  SortFailureAction,
  SortAction,
  DeleteSuccessAction,
  DeleteFailureAction,
  LoadProjectStatusesAction,
  LoadProjectStatusesSuccessAction,
  LoadProjectStatusesFailureAction,
} from './status.actions';
import { StatusSortModel } from '../../models/status-sort.model';
import { StatusModel } from '../../models/status.model';
import * as fromRoot from '../../redux/index';
import { StatusService } from '../../services/status.service';

@Injectable()
export class StatusEffects {
  private store = inject<Store<fromRoot.State>>(Store);
  private actions$ = inject(Actions);
  private statusService = inject(StatusService);

  search$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StatusActionTypes.SearchAction),
      switchMap(() =>
        this.statusService.findAll().pipe(
          map(status => new SearchSuccessAction(status)),
          catchError(errors => of(new SearchFailureAction(errors)))
        )
      )
    )
  );

  loadProjectStatuses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StatusActionTypes.LoadProjectStatusesAction),
      map((action: LoadProjectStatusesAction) => action.payload),
      switchMap((projectId: string) =>
        this.statusService.findAll(projectId).pipe(
          map(status => new LoadProjectStatusesSuccessAction(status)),
          catchError(errors => of(new LoadProjectStatusesFailureAction(errors)))
        )
      )
    )
  );

  save$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StatusActionTypes.SaveAction),
      map((action: SaveAction) => action.payload),
      switchMap((model: StatusModel) =>
        this.statusService.save(model).pipe(
          map(status => new SaveSuccessAction(status)),
          catchError(errors => of(new SaveFailureAction(errors)))
        )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StatusActionTypes.CreateAction),
      map((action: CreateAction) => action.payload),
      switchMap((model: StatusModel) =>
        this.statusService.create(model).pipe(
          map(status => new CreateSuccessAction(status)),
          catchError(errors => of(new CreateFailureAction(errors)))
        )
      )
    )
  );

  sort$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StatusActionTypes.SortAction),
      map((action: SortAction) => action.payload),
      switchMap((model: StatusSortModel) =>
        this.statusService.sort(model).pipe(
          map(status => new SortSuccessAction(status)),
          catchError(errors => of(new SortFailureAction(errors)))
        )
      )
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StatusActionTypes.DeleteAction),
      map((action: DeleteAction) => action.payload),
      switchMap((model: StatusModel) =>
        this.statusService.delete(model).pipe(
          map(() => new DeleteSuccessAction(model.id)),
          catchError(errors => of(new DeleteFailureAction(errors)))
        )
      )
    )
  );
}
